import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BrandMark, { logoForBrand, useBrandLogos } from '../components/BrandMark';
import {
  Icon,
  JobCard,
  JobFlags,
  JobWhen,
  Plate,
  QuickAction,
  StageAge,
  StageBadge,
  StageTrack,
  vehicleName,
} from '../components/JobUI';
import ViewTabs from '../components/ViewTabs';
import WalkInSheet from '../components/WalkInSheet';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { useJobs } from '../hooks/useJobs';
import { useServiceCatalog } from '../hooks/useServiceCatalog';
import { displayName } from '../lib/format';
import { JOB_FILTERS, JOB_STAGES } from '../lib/jobStage';

// Kept for existing importers (resale pages).
export {
  BOOKING_SELECT,
  bookingReceptionComplete,
  hydrateReceptionFlags,
  updateBookingStatus,
} from '../lib/bookingData';

const FILTER_ORDER = ['today', 'active', 'attention', 'waiting', 'ready', 'upcoming', 'completed', 'cancelled', 'all'];
const NEWEST_FIRST = new Set(['completed', 'cancelled', 'all']);

function dayStamp(date) {
  if (!date) return '';
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export default function BookingsPage() {
  const t = useT();
  const { profile } = useAuth();
  const { serviceLabel, serviceSearchHay } = useServiceCatalog();
  const makes = useBrandLogos();
  const { jobs, loading, error, now, live, reload } = useJobs('all');
  const [params, setParams] = useSearchParams();
  const isMechanic = profile?.role === 'technician';

  const view = FILTER_ORDER.includes(params.get('view')) ? params.get('view') : 'today';
  const [query, setQuery] = useState('');
  const [mineOnly, setMineOnly] = useState(isMechanic);
  const [layout, setLayout] = useState('list');
  const [datesOpen, setDatesOpen] = useState(false);
  const [walkIn, setWalkIn] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  function setView(next) {
    const copy = new URLSearchParams(params);
    copy.set('view', next);
    setParams(copy, { replace: true });
  }

  const searched = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return jobs.filter((job) => {
      if (mineOnly && !job.isMine) return false;
      if (dateFrom || dateTo) {
        const stamp = dayStamp(job.scheduledAt);
        if (!stamp) return false;
        if (dateFrom && stamp < dateFrom) return false;
        if (dateTo && stamp > dateTo) return false;
      }
      if (!needle) return true;
      const { row } = job;
      const hay = [
        displayName(row.profiles),
        row.profiles?.email,
        row.profiles?.phone,
        serviceSearchHay(row.service_type),
        row.vehicles?.brand,
        row.vehicles?.model,
        row.vehicles?.year,
        row.vehicles?.plate_number,
        row.branches?.name,
        job.mechanicName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [jobs, query, mineOnly, dateFrom, dateTo, serviceSearchHay]);

  const counts = useMemo(() => {
    const next = {};
    for (const id of FILTER_ORDER) {
      next[id] = searched.filter((job) => JOB_FILTERS[id](job, now)).length;
    }
    return next;
  }, [searched, now]);

  const visible = useMemo(() => {
    const list = searched.filter((job) => JOB_FILTERS[view](job, now));
    const dir = NEWEST_FIRST.has(view) ? -1 : 1;
    return list.sort(
      (a, b) => dir * ((a.scheduledAt?.getTime() || 0) - (b.scheduledAt?.getTime() || 0)),
    );
  }, [searched, view, now]);

  return (
    <section className="jobs-page">
      <header className="page-head">
        <div>
          <h1>{t('jobs.title')}</h1>
          <p className="lede">{t('jobs.lede')}</p>
        </div>
        <div className="head-actions">
          {live ? <span className="live-dot">{t('live.on')}</span> : null}
          {!isMechanic ? (
            <button type="button" className="primary" onClick={() => setWalkIn(true)}>
              {t('walkin.button')}
            </button>
          ) : null}
        </div>
      </header>
      {walkIn ? <WalkInSheet onClose={() => setWalkIn(false)} /> : null}

      {error ? <p className="form-error">{error}</p> : null}

      <div className="jobs-filters" role="tablist" aria-label={t('jobs.title')}>
        {FILTER_ORDER.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={view === id}
            className={`jobs-filter${view === id ? ' on' : ''}${id === 'attention' && counts[id] ? ' has-attn' : ''}`}
            onClick={() => setView(id)}
          >
            {t(`jobs.f.${id}`)}
            <em>{loading ? '' : counts[id]}</em>
          </button>
        ))}
      </div>

      <div className="jobs-toolbar">
        <label className="jobs-search">
          <Icon name="search" />
          <input
            type="search"
            placeholder={t('jobs.search')}
            aria-label={t('jobs.search')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {isMechanic ? (
          <label className="toggle-chip">
            <input type="checkbox" checked={mineOnly} onChange={(event) => setMineOnly(event.target.checked)} />
            <span>{t('today.mine')}</span>
          </label>
        ) : null}
        <button
          type="button"
          className={`toggle-chip${datesOpen || dateFrom || dateTo ? ' on' : ''}`}
          aria-expanded={datesOpen}
          onClick={() => setDatesOpen((open) => !open)}
        >
          <Icon name="date_range" />
          <span>{t('jobs.dates')}</span>
        </button>
        <ViewTabs
          value={layout}
          onChange={setLayout}
          items={[
            { id: 'list', label: t('jobs.view.list') },
            { id: 'board', label: t('jobs.view.board') },
          ]}
        />
      </div>

      {datesOpen ? (
        <div className="jobs-dates">
          <label className="field-shell">
            <span>{t('bookings.from')}</span>
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="field-shell">
            <span>{t('bookings.to')}</span>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </label>
          {dateFrom || dateTo ? (
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
            >
              {t('jobs.clearDates')}
            </button>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <p className="empty">{t('jobs.loading')}</p>
      ) : visible.length === 0 ? (
        <p className="empty">{t('jobs.empty')}</p>
      ) : layout === 'board' ? (
        <div className="stage-board">
          {JOB_STAGES.map((stage) => {
            const cards = visible.filter((job) => job.stage === stage);
            if (!cards.length) return null;
            return (
              <div key={stage} className={`lane stage-${stage}`}>
                <header className="lane-head">
                  <StageBadge stage={stage} />
                  <em>{cards.length}</em>
                </header>
                {cards.map((job) => (
                  <JobCard
                    key={job.row.id}
                    job={job}
                    showStage={false}
                    makes={makes}
                    serviceLabel={serviceLabel}
                    onChanged={reload}
                  />
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="job-table" role="table" aria-label={t('jobs.title')}>
          <div className="job-table-head" role="row">
            <span role="columnheader">{t('jobs.col.time')}</span>
            <span role="columnheader">{t('jobs.col.vehicle')}</span>
            <span role="columnheader">{t('jobs.col.customer')}</span>
            <span role="columnheader">{t('jobs.col.service')}</span>
            <span role="columnheader">{t('jobs.col.mechanic')}</span>
            <span role="columnheader">{t('jobs.col.stage')}</span>
            <span role="columnheader">{t('jobs.col.next')}</span>
          </div>
          {visible.map((job) => {
            const { row } = job;
            return (
              <div key={row.id} className={`job-table-row stage-${job.stage}`} role="row">
                <span role="cell" className="jt-when">
                  <JobWhen job={job} />
                </span>
                <span role="cell" className="jt-car">
                  <BrandMark name={row.vehicles?.brand} src={logoForBrand(makes, row.vehicles?.brand)} compact />
                  <span>
                    <Link to={`/bookings/${row.id}`} className="card-link">
                      <strong>{vehicleName(row.vehicles, t)}</strong>
                    </Link>
                    <Plate value={row.vehicles?.plate_number} />
                  </span>
                </span>
                <span role="cell" className="jt-customer">
                  <strong>{displayName(row.profiles)}</strong>
                  {row.profiles?.phone ? <small>{row.profiles.phone}</small> : null}
                </span>
                <span role="cell" className="jt-service">
                  {serviceLabel(row.service_type)}
                </span>
                <span role="cell" className={`jt-mechanic${job.mechanicName ? '' : ' none'}`}>
                  <Icon name="engineering" />
                  {job.mechanicName || t('assign.unassigned')}
                </span>
                <span role="cell" className="jt-stage">
                  <StageBadge stage={job.stage} />
                  <StageTrack stage={job.stage} />
                  <StageAge job={job} />
                  <JobFlags job={job} />
                </span>
                <span role="cell" className="jt-next">
                  <QuickAction job={job} onDone={reload} />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
